import { useEffect, useState } from "react";
import type { OrderCutoffStatus, OrderStatus, OrderSummary, ProductionReport } from "@platform/contracts";
import { api } from "../api";
import { useLiveData } from "../useLiveData";
import type { NotificationKind } from "./Portal";

const TRACKED: [OrderStatus, string][] = [["NEW", "New"], ["PROCESSING", "Printed"], ["PENDING_PAYMENT", "Pending payment"], ["COMPLETED", "Completed"]];

/** The morning question this answers: what is coming in today, what has to be
 *  made, and has the cut-off passed. */
export function StaffOverview({ onMessage }: { onMessage: (message: string, kind?: NotificationKind) => void }) {
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [report, setReport] = useState<ProductionReport | null>(null);
  const [cutoff, setCutoff] = useState<OrderCutoffStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const [orderResult, productionResult, timing] = await Promise.all([
      api.orders(), api.productionReport(today).catch(() => null), api.orderCutoff().catch(() => null)
    ]);
    setOrders(orderResult.orders); setReport(productionResult); setCutoff(timing); setLoading(false);
  };
  useEffect(() => { load().catch((error: Error) => { onMessage(error.message, "error"); setLoading(false); }); }, []);
  useLiveData(load);

  const todays = orders.filter((order) => order.createdAt.slice(0, 10) === today);
  const count = (status: OrderStatus) => todays.filter((order) => order.status === status).length;
  const revenue = todays.filter((order) => order.status !== "CANCELLED").reduce((sum, order) => sum + order.totalCents, 0);
  const topItems = (report?.items ?? []).slice().sort((a, b) => b.totalQuantity - a.totalQuantity).slice(0, 6);
  const totalUnits = (report?.items ?? []).reduce((sum, item) => sum + item.totalQuantity, 0);

  return <section className="staff-overview">
    <div className="section-heading"><div><h2>Today</h2><p>{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p></div></div>

    {cutoff && <div className={`cutoff-banner ${cutoff.afterCutoff ? "" : "open"}`} role="status">
      <strong>{cutoff.afterCutoff ? "Cut-off has passed" : `Ordering open until ${cutoff.cutoffTime}`}</strong>
      <span>{cutoff.afterCutoff ? `Orders placed now are scheduled for ${cutoff.processingDate}. They stay editable for one hour after submission.` : `Orders placed before ${cutoff.cutoffTime} (${cutoff.timezone}) are for today, and stay editable until then.`}</span>
    </div>}

    <div className="overview-stats">
      {TRACKED.map(([status, label]) => <article key={status}><small>{label}</small><strong>{loading ? "—" : count(status)}</strong></article>)}
      <article className="accent"><small>Today's value</small><strong>{loading ? "—" : `€${(revenue / 100).toFixed(2)}`}</strong></article>
    </div>

    <div className="overview-production">
      <div className="section-heading"><div><h3>To produce today</h3><p>{report ? `${report.orderCount} order${report.orderCount === 1 ? "" : "s"} · ${totalUnits} unit${totalUnits === 1 ? "" : "s"} · cancelled excluded` : "Production totals unavailable."}</p></div></div>
      {loading ? <div className="skeleton-list">{[0, 1, 2].map((row) => <span key={row} className="skeleton-row"/>)}</div>
        : topItems.length ? <div className="production-list">{topItems.map((item) => <article key={item.productId}><span><strong>{item.productName}</strong><small>{item.categoryName}</small></span><strong className="qty">{item.totalQuantity}</strong></article>)}</div>
        : <p className="empty-state">No orders to produce today yet.</p>}
    </div>
  </section>;
}
